import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BookOpen, Clock, ChevronLeft, ChevronRight, Highlighter, Eraser, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * IELTS Reading CBT Simulator.
 * - 3 passages × ~5 questions
 * - 60-minute timer (auto-submit)
 * - Highlight tool: select text in the passage → wraps it in a yellow span (CBT realism)
 * - Eraser: click any highlight to remove it
 * - Per-passage tabs + per-question form
 */
const TOTAL_SECS = 60 * 60;

function applyHighlight(passageId, selection, setHighlights) {
  if (!selection || selection.isCollapsed) return;
  const range = selection.getRangeAt(0);
  const container = document.getElementById(`passage-body-${passageId}`);
  if (!container || !container.contains(range.commonAncestorContainer)) return;
  try {
    const mark = document.createElement("mark");
    mark.className = "bg-yellow-200 text-slate-900 cursor-pointer ielts-hl";
    mark.title = "Click to remove highlight";
    mark.dataset.passage = passageId;
    range.surroundContents(mark);
    selection.removeAllRanges();
  } catch (e) {
    // selection crosses element boundary — ignore
  }
  setHighlights((h) => ({ ...h, [passageId]: Date.now() }));
}

export default function IELTSReadingMock() {
  const { user } = useAuth();
  const nav = useNavigate();
  const format = user?.ielts_format || "computer";
  const [stage, setStage] = useState("brief"); // brief | running | done
  const [mock, setMock] = useState(null);
  const [activePassage, setActivePassage] = useState(0);
  const [answers, setAnswers] = useState({});
  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECS);
  const [highlightMode, setHighlightMode] = useState(true);
  const [, setHighlights] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const mainTimer = useRef(null);

  useEffect(() => { api.get("/ielts/reading-mock").then((r) => setMock(r.data)); }, []);

  useEffect(() => {
    if (stage !== "running") return;
    mainTimer.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) { clearInterval(mainTimer.current); submit(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(mainTimer.current);
  }, [stage]); // eslint-disable-line

  const onPassageClick = (e) => {
    if (highlightMode) {
      const sel = window.getSelection();
      if (sel && sel.toString().trim().length > 0 && mock) {
        applyHighlight(mock.passages[activePassage].id, sel, setHighlights);
      }
    } else {
      // eraser mode: clicking a mark unwraps it
      const tgt = e.target;
      if (tgt && tgt.tagName === "MARK" && tgt.classList.contains("ielts-hl")) {
        const parent = tgt.parentNode;
        while (tgt.firstChild) parent.insertBefore(tgt.firstChild, tgt);
        parent.removeChild(tgt);
        setHighlights((h) => ({ ...h, [mock.passages[activePassage].id]: Date.now() }));
      }
    }
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const submit = async () => {
    if (submitting || result) return;
    clearInterval(mainTimer.current);
    setSubmitting(true);
    try {
      const r = await api.post("/ielts/reading-mock/submit", {
        answers: Object.fromEntries(Object.entries(answers).map(([k, v]) => [k, String(v)])),
        format,
      });
      setResult(r.data);
      setStage("done");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  if (!mock) return <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  if (stage === "brief") {
    return (
      <div className="min-h-screen bg-slate-900 text-white p-6 md:p-10 flex items-center justify-center" data-testid="reading-brief">
        <div className="max-w-2xl space-y-6">
          <div className="flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-[#16A34A]" />
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-white/60">IELTS · {format === "paper" ? "Paper-based" : "Computer-based"}</div>
              <h1 className="font-display text-4xl">{mock.title}</h1>
            </div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-sm leading-relaxed space-y-2">
            <div className="font-semibold mb-1">Read before you begin</div>
            <ul className="space-y-1.5 text-white/85">
              <li>• 3 passages · {mock.total_questions} questions · {mock.duration_minutes} minutes total</li>
              <li>• Use the <span className="font-semibold text-yellow-300">Highlighter</span> tool to mark key sentences (CBT only)</li>
              <li>• There is <span className="text-[#FB7185] font-semibold">no transfer time</span> — answers are saved as you type</li>
              <li>• Each correct answer = 1 mark · Wrong answers do not lose marks</li>
              <li>• The test auto-submits when the timer ends</li>
            </ul>
          </div>
          <button onClick={() => setStage("running")} data-testid="begin-reading-btn"
            className="w-full px-7 py-4 rounded-2xl bg-[#16A34A] hover:bg-[#15803D] text-white font-bold text-lg">
            Begin Reading Test
          </button>
          <button onClick={() => nav("/app/ielts/mock")} className="w-full text-sm text-white/50 hover:text-white">Cancel</button>
        </div>
      </div>
    );
  }

  if (stage === "done" && result) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 md:p-10" data-testid="reading-result">
        <div className="max-w-4xl mx-auto space-y-5">
          <div className="bg-gradient-to-br from-[#16A34A] to-[#065F46] text-white rounded-3xl p-8">
            <div className="text-xs uppercase tracking-[0.3em] text-white/70">IELTS Reading Result</div>
            <div className="font-display text-7xl mt-2">Band {result.band}</div>
            <div className="text-sm text-white/80 mt-2">{result.correct}/{result.total} correct</div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {result.section_breakdown.map((s, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{s.part}</div>
                <div className="font-display text-2xl text-[#003049] mt-1">{s.correct}/{s.total}</div>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="font-semibold text-[#003049] mb-3">Answer review</div>
            <div className="grid sm:grid-cols-2 gap-2 max-h-[400px] overflow-y-auto">
              {result.per_question.map((q) => (
                <div key={q.n} className={`rounded-lg border p-3 text-sm ${q.is_correct ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">Q{q.n}</span>
                    {q.is_correct ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <span className="text-xs text-rose-700">→ {q.correct_answer}</span>}
                  </div>
                  <div className="text-xs text-slate-600 mt-1">Your answer: {q.your_answer || <span className="italic">blank</span>}</div>
                </div>
              ))}
            </div>
          </div>
          <button onClick={() => nav("/app")} className="px-5 py-2.5 rounded-full bg-[#003049] text-white text-sm">Dashboard</button>
        </div>
      </div>
    );
  }

  const p = mock.passages[activePassage];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="reading-running">
      {/* Top bar */}
      <div className="bg-[#003049] text-white px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BookOpen className="w-5 h-5 text-[#34D399]" />
          <div className="text-sm font-semibold">IELTS Reading · {p.title}</div>
        </div>
        <div className="flex items-center gap-3">
          {format === "computer" && (
            <div className="flex items-center gap-1.5 bg-white/10 rounded-full p-1">
              <button onClick={() => setHighlightMode(true)} data-testid="hl-mode-btn"
                className={`px-3 py-1 rounded-full text-xs inline-flex items-center gap-1.5 ${highlightMode ? "bg-yellow-300 text-slate-900" : "text-white/70"}`}>
                <Highlighter className="w-3.5 h-3.5" /> Highlight
              </button>
              <button onClick={() => setHighlightMode(false)} data-testid="erase-mode-btn"
                className={`px-3 py-1 rounded-full text-xs inline-flex items-center gap-1.5 ${!highlightMode ? "bg-rose-300 text-slate-900" : "text-white/70"}`}>
                <Eraser className="w-3.5 h-3.5" /> Eraser
              </button>
            </div>
          )}
          <Clock className="w-4 h-4 text-[#FB7185]" />
          <div className="font-mono text-lg tabular-nums" data-testid="reading-timer">{fmt(secondsLeft)}</div>
        </div>
      </div>
      {secondsLeft < 300 && secondsLeft > 0 && (
        <div className="bg-rose-600 text-white px-6 py-1.5 text-center text-xs flex items-center justify-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5" /> Less than 5 minutes remaining
        </div>
      )}

      {/* Passage tabs */}
      <div className="bg-white border-b border-slate-200 px-4 flex gap-1">
        {mock.passages.map((pp, i) => (
          <button key={pp.id} onClick={() => setActivePassage(i)}
            data-testid={`passage-tab-${i}`}
            className={`px-4 py-3 text-sm font-medium border-b-2 ${activePassage === i ? "border-[#16A34A] text-[#16A34A]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            Passage {i + 1}
          </button>
        ))}
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-0 overflow-hidden">
        {/* Passage (left) */}
        <div className="overflow-y-auto p-6 border-r border-slate-200 bg-white" onMouseUp={onPassageClick} onClick={onPassageClick}>
          <div className="max-w-prose mx-auto">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{p.title}</div>
            <div id={`passage-body-${p.id}`} data-testid={`passage-body-${activePassage}`}
              className="text-slate-800 text-[15px] leading-7 whitespace-pre-wrap select-text">
              {p.body}
            </div>
          </div>
        </div>

        {/* Questions (right) */}
        <div className="overflow-y-auto p-6 bg-slate-50">
          <div className="max-w-prose mx-auto space-y-3">
            {p.questions.map((q) => (
              <div key={q.n} className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-start gap-3">
                  <div className="font-bold text-slate-900 text-sm w-7">{q.n}.</div>
                  <div className="flex-1 space-y-2">
                    <div className="text-sm text-slate-800">{q.prompt}</div>
                    {q.type === "fill" && (
                      <input data-testid={`r-ans-${q.n}`}
                        value={answers[q.n] || ""}
                        onChange={(e) => setAnswers((a) => ({ ...a, [q.n]: e.target.value.toUpperCase() }))}
                        className="w-full border border-slate-200 rounded px-3 py-2 text-sm uppercase focus:outline-none focus:border-[#16A34A]"
                        placeholder={`Max ${q.max_words || 3} words`} />
                    )}
                    {q.type === "tfng" && (
                      <div className="flex gap-2 flex-wrap">
                        {["TRUE", "FALSE", "NOT GIVEN"].map((opt) => (
                          <button key={opt} onClick={() => setAnswers((a) => ({ ...a, [q.n]: opt }))}
                            data-testid={`r-ans-${q.n}-${opt.replace(" ", "_")}`}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${answers[q.n] === opt ? "bg-[#16A34A] text-white border-[#16A34A]" : "border-slate-200 text-slate-600 hover:border-[#16A34A]"}`}>
                            {opt}
                          </button>
                        ))}
                      </div>
                    )}
                    {q.type === "mcq" && (
                      <div className="space-y-1.5">
                        {q.choices.map((c, ci) => (
                          <label key={ci} className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer border ${String(answers[q.n]) === String(ci) ? "border-[#16A34A] bg-[#ECFDF5]" : "border-slate-200 hover:border-slate-300"}`}>
                            <input type="radio" data-testid={`r-ans-${q.n}-${ci}`} checked={String(answers[q.n]) === String(ci)} onChange={() => setAnswers((a) => ({ ...a, [q.n]: ci }))} className="mt-1" />
                            <span className="text-sm">{c}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer nav */}
      <div className="bg-white border-t border-slate-200 px-6 py-3 flex items-center justify-between">
        <button onClick={() => setActivePassage(Math.max(0, activePassage - 1))} disabled={activePassage === 0}
          data-testid="reading-prev"
          className="px-4 py-2 rounded-full border border-slate-200 disabled:opacity-40 text-sm inline-flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> Prev passage
        </button>
        <div className="text-xs text-slate-500">Passage {activePassage + 1} of {mock.passages.length}</div>
        {activePassage < mock.passages.length - 1 ? (
          <button onClick={() => setActivePassage(activePassage + 1)} data-testid="reading-next"
            className="px-4 py-2 rounded-full bg-[#003049] text-white text-sm inline-flex items-center gap-1">
            Next passage <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={submit} disabled={submitting} data-testid="reading-submit"
            className="px-5 py-2 rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Submit reading
          </button>
        )}
      </div>
    </div>
  );
}
